import { getStandardButtonStyles } from "../../styles/standardButtons";
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
        TextField,
    makeStyles,
    FormControl,
    Typography,
    IconButton,
    InputAdornment,
    Tooltip,
    Select,
    MenuItem,
    InputLabel,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PeopleIcon from "@material-ui/icons/People";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import ScheduleIcon from "@material-ui/icons/Schedule";
import StandardTabs from "../StandardTabs";
import Collapse from "@material-ui/core/Collapse";
import Box from "@material-ui/core/Box";
import ProfessionalSchedulesForm from "../ProfessionalSchedulesForm";
import ProfessionalAvailabilityTab from "../ProfessionalAvailabilityTab";

const useStyles = makeStyles((theme) => ({
    ...getStandardButtonStyles(theme),
    dialogPaper: {
        backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
        borderRadius: 16,
        border: `1px solid ${theme.palette.primary.main}40`,
        boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
        color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
        overflow: "hidden",
    },
    dialogHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${
            theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
        }`,
        "& h2": {
            display: "flex",
            alignItems: "center",
            fontSize: "1.15rem",
            fontWeight: 600,
            margin: 0,
            color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
        },
    },
    helpBox: {
        backgroundColor:
            theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
        borderBottom: `1px solid ${
            theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
        }`,
        padding: theme.spacing(1.5, 3),
    },
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const ProfessionalSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required(i18n.t("professionalModal.form.name_required")),
    description: Yup.string(),
    email: Yup.string().email(i18n.t("professionalModal.form.email_invalid")),
});

const ProfessionalModal = ({ open, onClose, professionalId }) => {
    const classes = useStyles();

    const initialState = {
        name: "",
        email: "",
        meetingLink: "",
        modality: "VIRTUAL",
        address: "",
        serviceIds: [],
        workSchedules: []
    };

    const [professional, setProfessional] = useState(initialState);
    const [showHelp, setShowHelp] = useState(false);
    const [services, setServices] = useState([]);
    const [tab, setTab] = useState(0); // 0: General, 1: Schedules
    const isMounted = useRef(true);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const { data } = await api.get("/services");
                setServices(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchServices();

        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        const fetchProfessional = async () => {
            console.log("ProfessionalModal: Fetching professional...", { professionalId, open });
            if (!professionalId) return;
            try {
                const { data } = await api.get(`/professionals/${professionalId}`);
                console.log("ProfessionalModal: Fetched data:", data);
                if (isMounted.current) {
                    const professionalServices = data.services ? data.services.map(s => s.id) : [];
                    const newValues = {
                        ...data,
                        serviceIds: professionalServices,
                        workSchedules: data.workSchedules || []
                    };
                    console.log("ProfessionalModal: Setting professional state:", newValues);
                    setProfessional(newValues);
                }
            } catch (err) {
                console.error("ProfessionalModal: Error fetching", err);
                toastError(err);
            }
        };

        fetchProfessional();
    }, [professionalId, open]);

    const handleClose = () => {
        onClose();
        setProfessional(initialState);
        setTab(0);
    };

    const handleSaveProfessional = async (values) => {
        try {
            if (professionalId) {
                await api.put(`/professionals/${professionalId}`, values);
                toast.success(i18n.t("professionalModal.toasts.updated"));
            } else {
                await api.post("/professionals", values);
                toast.success(i18n.t("professionalModal.toasts.created"));
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    className: classes.dialogPaper,
                }}
            >
                <div className={classes.dialogHeader}>
                    <h2>
                        <PeopleIcon style={{ marginRight: 10, fontSize: 24, color: "#00b4d8" }} />
                        {professionalId
                            ? i18n.t("professionalModal.title.edit")
                            : i18n.t("professionalModal.title.add")}
                    </h2>
                    <Box display="flex" alignItems="center">
                        <IconButton
                            size="small"
                            onClick={() => setShowHelp((prev) => !prev)}
                            title="Ayuda / Información"
                            style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="caption"
                            style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
                        >
                            BotMix | Profesionales
                        </Typography>
                        <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </div>

                <Collapse in={showHelp}>
                    <Box className={classes.helpBox}>
                        <Typography
                            variant="body2"
                            style={{
                                color: "#00b4d8",
                                lineHeight: 1.5,
                            }}
                        >
                            {i18n.t("professionalModal.helpSummary") || "Configura el perfil del especialista, servicios autorizados, link de videollamada y horarios de disponibilidad semanal para la agenda."}
                        </Typography>
                    </Box>
                </Collapse>
                <div style={{ display: 'flex', justifyContent: 'center', margin: "10px 0 14px 0" }}>
                  <StandardTabs
                    value={tab}
                    onChange={(e, val) => setTab(val)}
                    items={[
                      {
                        value: 0,
                        label: i18n.t("professionalModal.tabs.general") || "Datos Generales",
                        icon: <PersonOutlineIcon style={{ fontSize: 18 }} />,
                      },
                      {
                        value: 1,
                        label: i18n.t("professionalModal.tabs.availability") || "Disponibilidad",
                        icon: <ScheduleIcon style={{ fontSize: 18 }} />,
                      },
                    ]}
                  />
                </div>
                <Formik
                    initialValues={professional}
                    enableReinitialize={true}
                    validationSchema={ProfessionalSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveProfessional(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                {tab === 0 && (
                                    <>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("professionalModal.form.name")}
                                            autoFocus
                                            name="name"
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Field
                                            as={TextField}
                                            label={i18n.t("professionalModal.form.email")}
                                            name="email"
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <FormControl variant="outlined" margin="dense" fullWidth>
                                            <InputLabel id="modality-label">Modalidad de Atención</InputLabel>
                                            <Field
                                                as={Select}
                                                labelId="modality-label"
                                                name="modality"
                                                label="Modalidad de Atención"
                                            >
                                                <MenuItem value="VIRTUAL">Virtual (Videollamada)</MenuItem>
                                                <MenuItem value="PRESENCIAL">Presencial (Físico / Local)</MenuItem>
                                            </Field>
                                        </FormControl>

                                        {values.modality === "VIRTUAL" && (
                                            <Field
                                                as={TextField}
                                                label="🔗 Link de Videollamada (Google Meet / Zoom)"
                                                name="meetingLink"
                                                placeholder="https://meet.google.com/xxx-yyyy-zzz"
                                                helperText="Pega aquí el enlace de Google Meet, Zoom u otra plataforma para que el agente lo comparta automáticamente."
                                                variant="outlined"
                                                margin="dense"
                                                fullWidth
                                            />
                                        )}

                                        {values.modality === "PRESENCIAL" && (
                                            <Field
                                                as={TextField}
                                                label="📍 Dirección Física / Local"
                                                name="address"
                                                placeholder="Ej: Av. Principal 123, Consultorio 4B"
                                                helperText="Escribe la dirección donde se atenderá la cita para que el agente la envíe al cliente."
                                                variant="outlined"
                                                margin="dense"
                                                fullWidth
                                            />
                                        )}

                                        {/* [ANTIGRAVITY] LINK DE AGENDAMIENTO AUTO-GENERADO */}
                                        {professionalId && professional.companyId && (
                                            <div style={{ marginTop: 16, marginBottom: 16, padding: "14px 16px", backgroundColor: "rgba(100, 120, 220, 0.06)", borderRadius: 8, border: "1px dashed rgba(100, 120, 220, 0.5)" }}>
                                                <Typography variant="caption" style={{ fontWeight: 700, color: "#9fa8da", display: "block", marginBottom: 4, fontSize: 13 }}>
                                                    🔗 Links de Agendamiento (solo lectura)
                                                </Typography>
                                                <Typography variant="caption" style={{ color: "#999", display: "block", marginBottom: 10, fontSize: 11 }}>
                                                    Copia el link y úsalo en el prompt del agente IA o en la base de conocimiento (RAG).
                                                </Typography>
                                                {values.serviceIds && values.serviceIds.length > 0 ? (
                                                    values.serviceIds.map(sId => {
                                                        const serviceName = services.find(s => s.id === sId)?.name || `Servicio ${sId}`;
                                                        const bookingUrl = `${window.location.origin}/booking/${professional.companyId}?professionalId=${professionalId}&serviceId=${sId}`;
                                                        return (
                                                            <div key={sId} style={{ marginBottom: 10 }}>
                                                                {/* Nombre del servicio */}
                                                                <Typography variant="caption" style={{ fontWeight: 600, color: "#9fa8da", display: "block", marginBottom: 4 }}>
                                                                    🛍️ {serviceName}
                                                                </Typography>
                                                                {/* Caja de URL visible */}
                                                                <div style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "#fff", border: "1px solid #90caf9", borderRadius: 6, padding: "6px 10px" }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        style={{
                                                                            flex: 1,
                                                                            fontFamily: "monospace",
                                                                            fontSize: 11,
                                                                            color: "#1565c0",
                                                                            wordBreak: "break-all",
                                                                            userSelect: "all",
                                                                        }}
                                                                    >
                                                                        {bookingUrl}
                                                                    </Typography>
                                                                    <Tooltip title="Copiar link">
                                                                        <IconButton
                                                                            size="small"
                                                                            style={{ padding: 4, color: "#1565c0" }}
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(bookingUrl);
                                                                                toast.success(`✅ Link de "${serviceName}" copiado!`);
                                                                            }}
                                                                        >
                                                                            <FileCopyOutlinedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Abrir en nueva pestaña">
                                                                        <IconButton
                                                                            size="small"
                                                                            style={{ padding: 4, color: "#1565c0" }}
                                                                            onClick={() => window.open(bookingUrl, "_blank")}
                                                                        >
                                                                            <span style={{ fontSize: 14 }}>↗</span>
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <Typography variant="body2" style={{ color: "#e65100", fontSize: 12 }}>
                                                        ⚠️ Selecciona al menos un servicio abajo para generar el link.
                                                    </Typography>
                                                )}
                                            </div>
                                        )}

                                        <FormControl
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        >
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                options={services}
                                                value={services.filter((s) => values.serviceIds && values.serviceIds.includes(s.id))}
                                                onChange={(e, newValue) => {
                                                    setFieldValue("serviceIds", newValue.map((v) => v.id));
                                                }}
                                                getOptionLabel={(option) => option.name}
                                                getOptionSelected={(option, value) => option.id === value.id}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        label={i18n.t("professionalModal.form.services")}
                                                        placeholder={i18n.t("professionalModal.form.servicesPlaceholder")}
                                                    />
                                                )}
                                            />
                                        </FormControl>
                                    </>
                                )}
                                {tab === 1 && (
                                    <ProfessionalAvailabilityTab
                                        professionalId={professionalId}
                                    />
                                )}
                            </DialogContent>
                            <DialogActions style={{ padding: "16px 24px" }}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className={classes.dangerButton}
                                >
                                    {i18n.t("common.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={classes.applyButton}
                                >
                                    {professionalId
                                        ? i18n.t("common.save")
                                        : i18n.t("common.add")}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default ProfessionalModal;
